import type { IQualityFieldItem } from "@agrigate/commodity-transaction-api/lib/services/CollectionTargetsService/dto";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import {
    formatAddr,
    formatExpectedQuantity,
} from "@agrigate/shared/lib/utils/formatters";
import Logo from "@agrigate/shared-web-ui/lib/assets/images/logo.png";
import { Avatar } from "@agrigate/shared-web-ui/lib/components/ui/avatar";
import FullScreenMessage from "@agrigate/shared-web-ui/lib/FullScreenMessage";
import { usePromiseToast } from "@agrigate/shared-web-ui/lib/hooks/use-promise-toast";
import LoginGuard from "@agrigate/shared-web-ui/lib/LoginGuard";
import { sessionAtom } from "@agrigate/shared-web-ui/lib/stores/session";
import { getRPC } from "@agrigate/shared-web-ui/lib/utils/rpc";
import { joinSlash } from "@agrigate/ts-utils/lib/string";
import { numStr } from "@agrigate/ts-utils/lib/zod";
import { Box, Button, Flex, Input, Text, VStack } from "@chakra-ui/react";
import { useSetAtom } from "jotai";
import { startCase } from "lodash";
import { type ReactElement, useState } from "react";
import { StringParam, useQueryParam } from "use-query-params";

declare const ASSET_PROXY_BASE_URL: string;

export default function HomePage(): ReactElement {
    return (
        <LoginGuard>
            <QCForm />
        </LoginGuard>
    );
}

export function QCForm(): ReactElement | null {
    const [ctId] = useQueryParam("ctId", StringParam);
    const setSession = useSetAtom(sessionAtom);
    const [shipmentId] = useQueryParam("shId", StringParam);
    const [isComplete, setComplete] = useState(false);

    const findTargetT = useAsyncTask(
        async () => {
            if (!ctId) return null;
            const res = await getRPC().collectionTargets.findOneById({
                id: ctId,
                enrichments: {
                    produceType: true,
                    variety: true,
                    pricingConditions: true,
                    collectionCenter: true,
                },
            });
            return res.target;
        },
        {
            deps: [ctId],
        },
    );

    const findShipmentT = useAsyncTask(
        async () => {
            if (!shipmentId) return null;
            const res = await getRPC().shipments.findById({
                id: shipmentId,
                enrichments: {
                    buyerOrder: true,
                    ingressStop: true,
                },
            });
            return res.shipment;
        },
        {
            deps: [shipmentId],
        },
    );

    const target = findTargetT.data;
    const shipment = findShipmentT.data;

    const [qualityFields, setQualityFields] = useState<IQualityFieldItem[]>([]);

    const cc = target?.collectionCenter?.value ?? shipment?.ingressStop?.value;

    const findManagersT = useAsyncTask(
        async () => {
            const ccId = cc?.id;
            if (!ccId) return;
            const res = await getRPC().users.findManagersForCollectionCenter({
                collectionCenterId: ccId,
            });
            return res.users;
        },
        {
            deps: [cc?.id],
        },
    );

    const cps = findManagersT.data?.filter(_ => !_.roles.includes("EMPLOYEE"));

    const { withToast } = usePromiseToast();

    const submitFields = () =>
        withToast(
            async () => {
                if (target) {
                    await getRPC().collectionTargets.submitQualityParameters({
                        collectionTargetId: target.id,
                        qualityFields,
                    });
                    setComplete(true);
                } else if (shipment) {
                    await getRPC().shipments.submitQualityParameters({
                        shipmentId: shipment.id,
                        qualityFields,
                    });
                    setComplete(true);
                }
            },
            { messages: true },
        );

    const setQualityValue = (
        itemId: string,
        fieldName: string,
        value: string,
    ) => {
        if (numStr.safeParse(value).error) return;
        setQualityFields(prev => {
            let didFind = false;
            const next = prev.map(prev => {
                if (prev.itemId === itemId && prev.fieldName === fieldName) {
                    didFind = true;
                    return {
                        ...prev,
                        value,
                    };
                } else return prev;
            });
            if (!didFind)
                next.push({
                    itemId,
                    fieldName,
                    value,
                });
            return next;
        });
    };

    if (!target) return null;

    if (isComplete)
        return (
            <FullScreenMessage
                label={
                    <>
                        <Text fontSize="xx-large">Thank you.</Text>
                        <Text mt={4} fontSize="md">
                            Your details have been submitted.
                        </Text>
                    </>
                }
            />
        );

    return (
        <Box
            bg="gray.50"
            minH="100vh"
            w="100vw"
            maxW="500px"
            marginLeft="auto"
            marginRight="auto"
        >
            {/* Header */}
            <Box
                bg="#f2eb46"
                px={5}
                py={2.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <Box>
                    <img
                        src={Logo}
                        alt="Agrigate"
                        style={{ height: "44px", width: "117px" }}
                    />
                </Box>
                {/*<Button
                    size="sm"
                    variant="outline"
                    borderColor="#3b815b"
                    color="#0a6132"
                    borderRadius="20px"
                    fontSize="10px"
                    px={3}
                >
                    <Box as="span" fontSize="18px" mr={1}>📞</Box>
                    Help
                </Button>*/}
            </Box>

            {/* Main Content */}
            <Box p={4} width="100%" maxW="100%">
                {/* Shipment ID */}
                <Text
                    fontSize="20px"
                    fontWeight="medium"
                    fontFamily="Poppins"
                    mb={2}
                >
                    {target.pubId}
                </Text>

                <VStack
                    gap={4}
                    align="stretch"
                    width="calc(100% - 8px)"
                    maxW="calc(100% - 8px)"
                >
                    {/* CP Details Card */}
                    <Box bg="white" borderRadius="12px" p={4} boxShadow="sm">
                        <Text fontSize="10px" color="gray.500" mb={2}>
                            Channel Partners
                        </Text>
                        {cps?.map(cp => (
                            <Flex
                                justify="space-between"
                                align="center"
                                key={cp.id}
                                mb={4}
                            >
                                <Flex gap={3} align="center">
                                    <Avatar
                                        name="DA"
                                        bg="pink.600"
                                        color="white"
                                        size="md"
                                    />
                                    <Box>
                                        <Text
                                            fontSize="13px"
                                            fontWeight="medium"
                                            fontFamily="Poppins"
                                        >
                                            {cp.fullName}
                                        </Text>
                                        <Text fontSize="10px" color="gray.600">
                                            {cp.phoneNumbers?.values?.join(
                                                ", ",
                                            )}
                                        </Text>
                                    </Box>
                                </Flex>
                                <Button
                                    size="md"
                                    variant="outline"
                                    borderColor="gray.300"
                                    fontSize="12px"
                                    asChild
                                >
                                    <a
                                        href={`tel:${cp.phoneNumbers?.values?.[0]}`}
                                    >
                                        <Box as="span" fontSize="14px" mr={1}>
                                            📞
                                        </Box>
                                        Call
                                    </a>
                                </Button>
                            </Flex>
                        ))}
                    </Box>

                    {/* Collection Center Card */}
                    <Box bg="white" borderRadius="12px" p={4} boxShadow="sm">
                        <Text fontSize="10px" color="gray.500" mb={2}>
                            Collection Center
                        </Text>
                        <Box mb={5}>
                            <Text
                                fontSize="13px"
                                fontWeight="medium"
                                fontFamily="Poppins"
                                mb={1}
                            >
                                {cc?.name}
                            </Text>
                            <Text fontSize="10px" color="gray.600">
                                {formatAddr({
                                    state: cc?.state,
                                    district: cc?.district,
                                    cityOrVillage: cc?.city,
                                    pinCode: cc?.pinCode,
                                    areaDetails: {
                                        address: cc?.address,
                                    },
                                })}
                            </Text>
                        </Box>
                        {cc?.lat && cc.long ? (
                            <Button
                                w="full"
                                size="md"
                                variant="outline"
                                borderColor="gray.300"
                                fontSize="12px"
                                asChild
                            >
                                <a
                                    href={`geo:${cc.lat},${cc.long}?q=${cc.lat},${cc.long}(${encodeURIComponent(cc.name)})`}
                                >
                                    <Box as="span" fontSize="20px" mr={1}>
                                        ↗️
                                    </Box>
                                    Get Directions
                                </a>
                            </Button>
                        ) : null}
                    </Box>

                    {/* Product Card */}
                    <Box bg="white" borderRadius="12px" p={4} boxShadow="sm">
                        {target.items.values.map(item => {
                            const pt = item.produceType?.value;
                            const imagePath = pt?.imagePath;

                            return (
                                <Flex gap={3} align="center" key={item.id}>
                                    {imagePath ? (
                                        <Box
                                            w="44px"
                                            h="44px"
                                            borderRadius="6px"
                                            overflow="hidden"
                                            bg="gray.200"
                                        >
                                            <img
                                                src={joinSlash(
                                                    ASSET_PROXY_BASE_URL,
                                                    imagePath,
                                                )}
                                                alt="Paddy"
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </Box>
                                    ) : null}

                                    <Flex
                                        flex={1}
                                        justify="space-between"
                                        align="center"
                                    >
                                        <Flex direction="column">
                                            <Text
                                                fontSize="13px"
                                                fontWeight="medium"
                                                fontFamily="Poppins"
                                            >
                                                {item.produceType?.value?.name}
                                            </Text>
                                            <Text
                                                fontSize="13px"
                                                fontWeight="medium"
                                                fontFamily="Poppins"
                                            >
                                                {item.variety?.value?.name}
                                            </Text>
                                        </Flex>
                                        <Flex gap={2} align="center">
                                            <Flex gap={1.5} align="center">
                                                <Box as="span" fontSize="15px">
                                                    ⚖️
                                                </Box>
                                                <Text
                                                    fontSize="13px"
                                                    color="#5c5c5c"
                                                >
                                                    {formatExpectedQuantity(
                                                        item,
                                                    )}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            );
                        })}
                    </Box>

                    {/* Quality Parameters Card */}

                    {target.items.values.map(item => {
                        const pt = item.produceType?.value;

                        return (
                            <Box
                                bg="white"
                                borderRadius="12px"
                                p={4}
                                boxShadow="sm"
                                key={item.id}
                            >
                                {/* Header */}
                                <Flex
                                    justify="space-between"
                                    mb={2}
                                    fontSize="13px"
                                    fontWeight="medium"
                                    color="#737373"
                                >
                                    <Text>Parameters</Text>
                                    <Flex gap={2}>
                                        <Text w="80px" textAlign="center">
                                            Value
                                        </Text>
                                    </Flex>
                                </Flex>

                                {pt?.fields?.fields?.map(f => {
                                    const prevVal =
                                        qualityFields.find(
                                            _ =>
                                                _.itemId === item.id &&
                                                _.fieldName === f.name,
                                        )?.value ?? "";
                                    return (
                                        <Flex
                                            justify="space-between"
                                            align="center"
                                            mb={2}
                                            key={f.name}
                                        >
                                            <Text
                                                fontSize="13px"
                                                fontWeight="medium"
                                                color="#282e38"
                                            >
                                                {startCase(f.name)}
                                            </Text>
                                            <Flex gap={2}>
                                                <Input
                                                    w="80px"
                                                    h="36px"
                                                    textAlign="center"
                                                    defaultValue="0 %"
                                                    fontSize="13px"
                                                    fontWeight="medium"
                                                    color="#737373"
                                                    borderColor="#d5d5d5"
                                                    borderRadius="4px"
                                                    value={prevVal}
                                                    onInput={e => {
                                                        setQualityValue(
                                                            item.id,
                                                            f.name,
                                                            e.currentTarget
                                                                .value,
                                                        );
                                                    }}
                                                />
                                            </Flex>
                                        </Flex>
                                    );
                                })}

                                {/* Submit Button */}
                                <Button
                                    w="full"
                                    h="50px"
                                    variant="outline"
                                    borderColor="#bbf7d0"
                                    color="#116932"
                                    fontSize="12px"
                                    fontWeight="medium"
                                    onClick={submitFields}
                                >
                                    Submit Quality Parameters
                                </Button>
                            </Box>
                        );
                    })}

                    <Button
                        w="full"
                        h="50px"
                        variant="outline"
                        borderColor="#fecaca"
                        color="#991919"
                        fontSize="12px"
                        fontWeight="medium"
                        onClick={() => {
                            setSession(null);
                        }}
                    >
                        Log out
                    </Button>
                </VStack>
            </Box>
        </Box>
    );
}
